import "handsontable/dist/handsontable.full.css";

import {
  Button,
  FormControl,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import FormError, { useError } from "../components/FormError";
import { get, startCase } from "lodash";

import ConfirmationDialog from "../components/ConfirmationDialog";
import Handsontable from "handsontable";
import { HotTable } from "@handsontable/react";
import React from "react";
import api from "../api";
import dayjs from "dayjs";
import displayPhone from "../modules/displayPhone";
import { localStorageCache } from "../lithic/localStorageHelper";
import { propertiesManagementSystems } from "./PartnerDetailPage/constants";
import { useTheme } from "@material-ui/core/styles";
import useToggle from "../state/useToggle";
import config from "../config";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import PreOptOutUpload from "./PartnerDetailPage/components/PreOptOutUpload";

export default function ExclusionListPage() {
  const tableRef = React.useRef();
  const changeToggle = useToggle();
  const addToggle = useToggle();
  const theme = useTheme();
  const classes = useStyles();
  const { id: partnerId } = useParams();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(500);
  const [search, setSearch] = React.useState("");

  const [debouncedSearch] = useDebouncedValue(search);

  const [data, setData] = React.useState(null);
  const [changes, setChanges] = React.useState(null);

  const [formMessage, setFormMessage] = useError();

  const [partner, setPartner] = React.useState({});

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  function addExclusion(exclusion) {
    setFormMessage("");
    return api
      .createExclusion(partnerId, exclusion)
      .then(() => api.getPartnerExclusions(partnerId))
      .then((resp) => {
        return (
          setData({ items: resp.data.items, totalCount: resp.data.totalCount }),
          addToggle.turnOff()
        );
      })
      .catch((error) => {
        let errorMsg;
        if (get(error, "response.status") === 400) {
          const msg = get(error, "response.data.error.message");
          if (msg === "email is already taken")
            errorMsg = "It looks like partner with this email already exists";
        }
        setFormMessage(errorMsg || error);
      });
  }

  function deleteExclusion(exclusionId) {
    setFormMessage("");
    api
      .deleteExclusion(partnerId, exclusionId)
      .then(refreshExclusionList)
      .catch((error) => {
        setFormMessage(
          get(error, "response.data.error.message") || error.message || error
        );
      });
  }

  const hotInstance = tableRef.current?.hotInstance;

  React.useEffect(() => {
    refreshExclusionList();
  }, [page, debouncedSearch, rowsPerPage]);

  function refreshExclusionList() {
    api
      .getPartnerExclusions(partnerId, {
        page: page + 1,
        search: debouncedSearch,
        perPage: rowsPerPage
      })
      .then((resp) => {
        setData({ items: resp.data.items, totalCount: resp.data.totalCount });
      });
  }

  React.useEffect(() => {
    api.getPartner({ id: partnerId }).then(api.pickData).then(setPartner);
  }, [partnerId]);

  function cellCustomRenderer(
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties
  ) {
    Handsontable.renderers.TextRenderer.apply(this, arguments);
    td.style.fontSize = theme.typography.fontSizeTable;
  }

  function dateRenderer(instance, td, row, col, prop, value, cellProperties) {
    Handsontable.renderers.TextRenderer.apply(this, arguments);
    td.innerHTML = value ? dayjs(value).format("MMM DD, YYYY") : "";
    td.style.fontSize = theme.typography.fontSizeTable;
    return td;
  }

  function phoneRenderer(instance, td, row, col, prop, value, cellProperties) {
    Handsontable.renderers.TextRenderer.apply(this, arguments);
    td.innerHTML = value ? displayPhone(value) : "";
    td.style.fontSize = theme.typography.fontSizeTable;
    return td;
  }

  function deleteButtonRenderer(
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties
  ) {
    Handsontable.renderers.BaseRenderer.apply(this, arguments);

    td.innerHTML = "";
    const button = document.createElement("button");
    button.innerText = "Delete";
    button.style.cursor = "pointer";
    button.style.color = "white";
    button.style.backgroundColor = "#f44336";
    button.style.border = "none";
    button.style.padding = "5px 10px";
    button.style.borderRadius = "4px";

    button.onclick = function (e) {
      e.stopPropagation();
      const rowData = instance.getSourceDataAtRow(row);
      if (
        window.confirm(
          `Are you sure you want to delete ${
            rowData.email || "this exclusion"
          }?`
        )
      )
        deleteExclusion(rowData.id);
    };

    td.appendChild(button);
    td.style.textAlign = "center";
    td.style.verticalAlign = "middle";
  }

  const tableSetttings = {
    data: data?.items,
    className: "htCenter htMiddle handsontable",
    rowHeaders: true,
    columns: [
      {
        data: "id",
        title: "Id",
        renderer: Handsontable.renderers.TextRenderer
      },
      {
        data: "createdAt",
        title: "Created",
        renderer: dateRenderer
      },
      {
        data: "firstName",
        title: "First Name",
        renderer: Handsontable.renderers.TextRenderer
      },
      {
        data: "lastName",
        title: "Last Name",
        renderer: Handsontable.renderers.TextRenderer
      },

      {
        data: "email",
        title: "Email",
        renderer: Handsontable.renderers.TextRenderer
      },
      {
        data: "phone",
        title: "Phone Number",
        renderer: phoneRenderer
      },
      {
        data: "source",
        title: "Source",
        renderer: Handsontable.renderers.TextRenderer
      },
      {
        data: null, // no binding needed
        title: "Actions",
        renderer: deleteButtonRenderer,
        readOnly: true,
        width: 100
      }
    ],
    afterGetColHeader: (column, TH) => {
      TH.style.fontWeight = "bold";
      TH.style.fontSize = theme.typography.fontSizeTable;
    },
    height: "75vh",
    renderer: cellCustomRenderer,
    width: "100%",
    rowHeights: 35,
    dropdownMenu: true,
    filters: true,
    columnSorting: true,
    fixedColumnsLeft: 2,
    overflow: "hidden",
    currentRowClassName: classes.activeRow,
    licenseKey: "non-commercial-and-evaluation",
    afterLoadData: (sourceData, initialLoad, source) => {
      const defaultValue = config.apiHost.includes("staging")
        ? [
            {
              column: 2,
              operation: "conjunction",
              conditions: [{ name: "by_value", args: [["active"]] }]
            }
          ]
        : [];
      let conditions = localStorageCache.getItem(
        "filterConditions",
        defaultValue
      );
      if (initialLoad) {
        localStorageCache.removeItem("filterConditions");
        localStorageCache.setItem("filterConditions", defaultValue);
      } else if (conditions.length) {
        let plugin = hotInstance.getPlugin("Filters");
        conditions.forEach((condition) => {
          plugin.addCondition(
            condition.column,
            condition.conditions[0].name,
            condition.conditions[0].args,
            condition.operation
          );
        });
      }
    },
    afterFilter: (conditionsStack) => {
      localStorageCache.setItem("filterConditions", conditionsStack);
    },
    afterSetDataAtCell: (changes, source) => {
      changes?.forEach(([row, prop, oldValue, newValue]) => {
        const currentRow = hotInstance.toPhysicalRow(row);
        const rowData = hotInstance.getSourceDataAtRow(currentRow);
        const id = rowData.id;
        const param = prop.includes(".") ? prop.split(".").pop() : prop;
        if (oldValue !== newValue && source === "edit") {
          setFormMessage();
          changeToggle.turnOn();
          setChanges({ id, param, newValue });
        }
      });
    }
  };

  return (
    <>
      <div className={classes.header}>
        <Typography variant="h5" color="primary">
          {partner.name}'s Exclusion List
        </Typography>
        <TextField
          label="Search"
          variant="outlined"
          onChange={(event) => setSearch(event.target.value)}
        />
        <div>
          <Button
            color="primary"
            variant="contained"
            onClick={addToggle.turnOn}
          >
            Add new exclusion
          </Button>
        </div>
      </div>
      <ExclusionForm
        toggle={addToggle}
        addExclusion={addExclusion}
        error={formMessage}
        resetError={setFormMessage}
      />

      <PreOptOutUpload
        partnerId={partnerId}
        onUploadSuccess={refreshExclusionList}
      />
      <HotTable settings={tableSetttings} ref={tableRef} />
      <TablePagination
        component="div"
        count={data?.totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[500, 1000]}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  activeRow: {
    background: theme.palette.primary.main
  },

  header: {
    margin: theme.spacing(2),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
  }
}));

function ExclusionForm({ toggle, addExclusion, error, resetError }) {
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [source, setSource] = React.useState("partner_requested");

  const newExclusion = {
    firstName,
    lastName,
    source,
    email,
    phone
  };

  const valid = email || phone;

  return (
    <ConfirmationDialog
      title="Add new partner"
      open={toggle.on}
      onCancel={toggle.turnOff}
      onConfirm={() => {
        addExclusion(newExclusion);
      }}
      cancelProps={{ children: "Go back" }}
      confirmProps={{ children: "Yes", disabled: !valid }}
    >
      <FormError error={error} resetError={resetError} />
      <FormControl margin="normal" fullWidth>
        <TextField
          label="First Name"
          type="text"
          value={firstName}
          variant="outlined"
          onChange={(e) => setFirstName(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          required
          label="Last Name"
          type="text"
          value={lastName}
          variant="outlined"
          onChange={(e) => setLastName(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          required
          label="Email"
          type="email"
          value={email}
          variant="outlined"
          onChange={(e) => setEmail(e.target.value)}
        />
      </FormControl>
      <FormControl margin="normal" fullWidth>
        <TextField
          label="phone"
          type="phone"
          value={phone}
          variant="outlined"
          onChange={(e) => setPhone(e.target.value)}
        />
      </FormControl>

      <FormControl margin="normal" fullWidth>
        <Select value={source} onChange={(e) => setSource(e.target.value)}>
          <MenuItem key="ss" value="typeform">
            typeform
          </MenuItem>
          <MenuItem key="fs" value="partner_requested">
            Partner Requested
          </MenuItem>
        </Select>
      </FormControl>
    </ConfirmationDialog>
  );
}
